// src/components/Admin/Campaigns/ai/useCampaignAi.ts
//
// React Query wrappers around the background Campaign Builder AI flows, plus
// local draft-acceptance ergonomics.
//
// Design rules:
//   - These are read-through caches only. A generation fires AT MOST ONCE for a
//     given grounding key and NEVER auto-writes anything back to a campaign or
//     step — the builder owns writes; the hooks only surface a draft the admin
//     can choose to accept.
//   - The query key carries a grounding hash of the input, so it re-fetches when
//     the source material genuinely changes but does not silently rewrite an
//     already-shown draft on unrelated re-renders.
//   - No Redux, no Zustand — this is stateless query caching plus a little local
//     `useState` for the on-demand field-note trigger.
//
// React Query v5 uses object-style query configuration and `gcTime`.

import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { Campaign, CampaignStep } from '../../../../support/campaigns';
import {
  CampaignOutlineDraft,
  StepStub,
  generateCampaignOutline,
  generateFieldNote,
  generateStepDescription,
  suggestChildSteps,
} from './campaignFlows';

// ---------------------------------------------------------------------------
// Grounding
// ---------------------------------------------------------------------------

/** A short, stable hash of an arbitrary string, for use inside a query key. */
function hash(input: string): string {
  let h = 5381;
  for (let i = 0; i < input.length; i += 1) {
    h = (h * 33) ^ input.charCodeAt(i);
  }
  // >>> 0 keeps it a non-negative 32-bit int.
  return (h >>> 0).toString(36);
}

const groundOutline = (input: { name: string; description?: string }): string =>
  hash(`${input.name || ''}::${input.description || ''}`);

const groundStep = (campaign: Campaign | null, step: CampaignStep | null): string =>
  hash(
    [
      campaign?.id ?? 'none',
      campaign?.name ?? '',
      step?.id ?? 'none',
      step?.name ?? '',
      step?.track ?? '',
      step?.step_kind ?? '',
      step?.description ?? '',
    ].join('::')
  );

const groundChildren = (
  campaign: Campaign | null,
  parentStep: CampaignStep | null,
  existingSteps: CampaignStep[]
): string =>
  hash(
    [
      campaign?.id ?? 'none',
      campaign?.name ?? '',
      parentStep?.id ?? 'root',
      (existingSteps || []).length,
      (existingSteps || []).map((s) => s.id).join(','),
    ].join('::')
  );

const groundField = (input: {
  entity: 'campaign' | 'step';
  field: string;
  context: Record<string, unknown>;
}): string => hash(`${input.entity}::${input.field}::${JSON.stringify(input.context || {})}`);

// A stable query timeout for the abortable flows. React Query passes the
// signal via the query-function context, so each flow is cancelled if its
// observer unmounts.
const SHARED_OPTS = {
  retry: false as const,
  staleTime: Infinity,
  gcTime: Infinity,
  refetchOnWindowFocus: false as const,
  refetchOnReconnect: false as const,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Draft a full campaign outline from a working name + description. */
export function useCampaignOutlineDraft(input: {
  name: string;
  description?: string;
  enabled: boolean;
}): {
  data: CampaignOutlineDraft | null;
  isLoading: boolean;
  error: unknown;
  refresh: () => void;
} {
  const queryClient = useQueryClient();
  const ground = groundOutline(input);
  const key = ['campaign-ai', 'outline', ground];

  const enabled = Boolean(input.enabled && (input.name || '').trim());

  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      generateCampaignOutline(
        { name: input.name, description: input.description },
        signal
      ),
    ...{ ...SHARED_OPTS, enabled },
  });

  const refresh = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: key });
  }, [queryClient, ground]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    data: (query.data ?? null) as CampaignOutlineDraft | null,
    isLoading: query.isFetching,
    error: query.error,
    refresh,
  };
}

/** Draft a description for a single step, grounded on its campaign. */
export function useStepDescriptionDraft(input: {
  campaign: Campaign;
  step: CampaignStep | null;
  enabled: boolean;
}): { data: string | null; isLoading: boolean; refresh: () => void } {
  const queryClient = useQueryClient();
  const ground = groundStep(input.campaign ?? null, input.step ?? null);
  const key = ['campaign-ai', 'step-description', ground];

  const enabled = Boolean(input.enabled && input.campaign && input.step);

  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      input.step
        ? generateStepDescription(
            { campaign: input.campaign, step: input.step },
            signal
          )
        : Promise.resolve(null),
    ...{ ...SHARED_OPTS, enabled },
  });

  const refresh = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: key });
  }, [queryClient, ground]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    data: (query.data ?? null) as string | null,
    isLoading: query.isFetching,
    refresh,
  };
}

/** Suggest child steps to add under a parent (or at the root) of a campaign. */
export function useChildStepSuggestions(input: {
  campaign: Campaign;
  parentStep: CampaignStep | null;
  existingSteps: CampaignStep[];
  enabled: boolean;
}): { data: StepStub[] | null; isLoading: boolean; refresh: () => void } {
  const queryClient = useQueryClient();
  const ground = groundChildren(
    input.campaign ?? null,
    input.parentStep ?? null,
    input.existingSteps || []
  );
  const key = ['campaign-ai', 'child-steps', ground];

  const enabled = Boolean(input.enabled && input.campaign);

  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      suggestChildSteps(
        {
          campaign: input.campaign,
          parentStep: input.parentStep,
          existingSteps: input.existingSteps || [],
        },
        signal
      ),
    ...{ ...SHARED_OPTS, enabled },
  });

  const refresh = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: key });
  }, [queryClient, ground]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    data: (query.data ?? null) as StepStub[] | null,
    isLoading: query.isFetching,
    refresh,
  };
}

/**
 * On-demand helper note for one field. Nothing runs until `generate()` is
 * called (even when `enabled` is true), so a note is never produced just by
 * mounting a form. `generate()` also re-runs when re-invoked after the grounding
 * changed.
 */
export function useFieldNote(input: {
  entity: 'campaign' | 'step';
  field: string;
  context: Record<string, unknown>;
  enabled: boolean;
}): { data: string | null; isLoading: boolean; generate: () => void } {
  const queryClient = useQueryClient();
  const ground = groundField(input);
  const key = ['campaign-ai', 'field-note', input.entity, input.field, ground];

  // Armed only after generate() is pressed; resets when the grounding changes so
  // a stale note is not shown against fresh field context.
  const [armed, setArmed] = React.useState(false);
  React.useEffect(() => {
    setArmed(false);
  }, [ground]);

  const enabled = Boolean(input.enabled && armed);

  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      generateFieldNote(
        { entity: input.entity, field: input.field, context: input.context },
        signal
      ),
    ...{ ...SHARED_OPTS, enabled },
  });

  const generate = React.useCallback(() => {
    if (armed) {
      // Already armed for this grounding: force a fresh draft.
      queryClient.invalidateQueries({ queryKey: key });
    } else {
      setArmed(true);
    }
  }, [armed, queryClient, ground]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    data: (query.data ?? null) as string | null,
    isLoading: query.isFetching,
    generate,
  };
}
